import { withRouter } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import { Button, Col, Row, Spinner } from "reactstrap";
import { connect } from "react-redux";
import { useCallback, useEffect, useMemo, useState } from "react";
import CreateProductSteps from './components/tabs'
import CreateProductTabContent from './components/tabs-content'
import { steps } from "./initiator";
import ProductContext from "./contexts/ProductContext";
import { useMutation, useQuery } from "@tanstack/react-query";
import IndustriesService from "src/modules/3rd-party-management/apis/common/IndustriesService";
import CommonService from "src/modules/3rd-party-management/apis/common/CommonService";
import { toast } from "react-toastify";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";
import SupplierProductService from "src/modules/3rd-party-management/apis/common/SupplierProductService";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import {
    confrimPromptUpdateReadyToShowState,
    confirmPromptUpdateContentState,
    confirmPromptResetState,
    confirmPromptHidePrompt
} from 'src/store/actions';
import { RiErrorWarningLine } from "react-icons/ri";
import CancelConfirmationModal from "./components/CancelConfirmationModal";

const STORAGE_SAVE_KEY = 'MODULE_TPDD_NEW_EUDR_PRODUCT_SAVED_DATA';

const CreateProductPage = ({
    t,
    lng,
    productId,
    supplierId,
    industryIds,
    exitConfirmPromptOptions,

    updateConfirmPromptReadyToShowState,
    updateConfirmPromptContent,
    resetConfirmPromptState,
    hideConfirmPrompt
}) => {
    const history = useHistory();

    const [ productData, setProductData ] = useState({});
    const [ tab, setTab ] = useState('information');

    const [ showCancelConfirmModal, setShowCancelConfirmModal ] = useState(false);
    const [ updateInprocess, setUpdateInprocess ] = useState(false);

    const removeDataFromStorage = () => {
        localStorage.removeItem(STORAGE_SAVE_KEY);
    }

    const fetchProductDetailsQuery = useQuery({
        queryKey: [
            '3rd-party-management-fetch-supplier-product-detail',
            productId
        ],
        queryFn: async () => {
            const service = SupplierProductService.getInstance();

            return await service.show(productId, {});
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: !!productId,
        onError: () => {
            toast(t('An error occurred while fetching product details.'), {
                type: 'error'
            });
        }
    });

    const fetchProductsQuery = useQuery({
        queryKey: [
            '3rd-party-management-fetch-supplier-industry-products-list',
            supplierId,
            industryIds
        ],
        queryFn: async () => {
            const service = IndustriesService.getInstance();

            return await service.fetchProducts({ selectedIndustryIds: industryIds }, lng);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching products list.'), {
                type: 'error'
            });
        }
    });

    const fetchEntitiesQuery = useQuery({
        queryKey: [
            '3rd-party-management-fetch-legal-entities-list',
            'creditor'
        ],
        queryFn: async () => {
            const service = CommonService.getInstance();
            return await service.fetchLegalEntities();
        },
        enabled: isFeatureEnabledForActiveModule('entity_management'),
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching entities.'), {
                type: 'error'
            });
        }
    });

    const fetchCountriesQuery = useQuery({
        queryKey: ['3rd-party-management-fetch-countries-list-query'],
        queryFn: async () => {
            const service = CommonService.getInstance();

            return await service.fetchCountries();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching countries list.'), {
                type: 'error'
            });
        }
    });

    const {
        mutate: createProductMutate,
        isLoading: productCreationIsLoading,
        isSuccess: createProductSucceed
    } = useMutation({
        mutationFn: async (payload) => {
            const service = SupplierProductService.getInstance();
            return await service.create(payload);
        },
        onSuccess: () => {
            removeDataFromStorage();
            resetConfirmPromptState();

            toast(t("New product created successfully."), {
                type: "success"
            });

            setTimeout(() => {
                history.push({
                    search: 'tab=products'
                });
            }, 250);
        },
        onError: () => {
            toast(t("An error occurred while creating product."), {
                type: "error"
            });
        }
    });

    const {
        mutate: updateProductMutate,
        isLoading: productUpdateIsLoading
    } = useMutation({
        mutationFn: async ({
            id,
            payload
        }) => {
            const service = SupplierProductService.getInstance();
            return await service.update(id, payload);
        },
        onSuccess: () => {
            toast(t("product updated successfully."), {
                type: "success"
            });

            setTimeout(() => {
                history.push({
                    search: 'tab=products'
                });
            }, 250);
        },
        onError: () => {
            toast(t("An error occurred while updating product."), {
                type: "error"
            });
        }
    });

    const productsList = useMemo(() => {
        let result = [];
        let products = fetchProductsQuery?.data;

        if (Array.isArray(products)) {
            result = products.map((product) => {
                return {
                    value: product.id,
                    label: `${product.class} - ${t(product.name)}`,
                    baseLabel: product.name,
                    commodity: product.commodity
                }
            });
        }

        return {
            isFetching: fetchProductsQuery.isFetching,
            data: result
        };

    }, [fetchProductsQuery, t]);

    const entitiesList = useMemo(() => {
        const result = (fetchEntitiesQuery?.data?.legalEntities || []).map((legal) => {
            return {
                value: legal.id,
                label: legal.title,
                shortName: legal.shortName,
                naceCode: legal.nace_code
            }
        });

        return {
            isFetching: fetchEntitiesQuery.isFetching,
            data: result
        }
    }, [ fetchEntitiesQuery ]);

    const countriesList = useMemo(() => {
        const result = (fetchCountriesQuery?.data || []).map((country) => {
            return {
                value: country.id,
                label: t(country.name),
                code: country.code
            };
        });

        return {
            isFetching: fetchCountriesQuery.isFetching,
            data: result
        }

    }, [fetchCountriesQuery, t]);

    const tabsToDisable = useMemo(() => {
        if (!productData?.name || productData.name.commodity) return [];

        return ['traceablility', 'geolocation'];
    }, [ productData?.name ]);

    useEffect(() => {
        if (fetchProductDetailsQuery?.data) {
            const naceCode = {
                value: fetchProductDetailsQuery.data.name.id,
                label: `${fetchProductDetailsQuery.data.name.class} - ${fetchProductDetailsQuery.data.name.name}`,
                baseLabel: fetchProductDetailsQuery.data.name.name,
                commodity: fetchProductDetailsQuery.data.commodity
            };

            const exportCountry = fetchProductDetailsQuery.data.exportToCountry?.id ? {
                value: fetchProductDetailsQuery.data.exportToCountry.id,
                label: fetchProductDetailsQuery.data.exportToCountry.name,
                baseLabel: fetchProductDetailsQuery.data.exportToCountry.name
            } : null;

            const importCountry = fetchProductDetailsQuery.data.importFromCountry?.id ? {
                value: fetchProductDetailsQuery.data.importFromCountry.id,
                label: fetchProductDetailsQuery.data.importFromCountry.name,
                baseLabel: fetchProductDetailsQuery.data.importFromCountry.name
            } : null;

            const originCountry = fetchProductDetailsQuery.data.originCountry?.id ? {
                value: fetchProductDetailsQuery.data.originCountry.id,
                label: fetchProductDetailsQuery.data.originCountry.name,
                baseLabel: fetchProductDetailsQuery.data.originCountry.name
            } : null;

            const legalEntitiesIds = (fetchProductDetailsQuery.data.legal_entities || []).map((legal) => {
                return {
                    value: legal.id,
                    label: legal.name
                }
            });

            setProductData({
                ...fetchProductDetailsQuery.data,
                name: naceCode,
                exportToCountry: exportCountry,
                importFromCountry: importCountry,
                originCountry: originCountry,
                legal_entity_ids: legalEntitiesIds,
                importDate: fetchProductDetailsQuery.data.importDate ? new Date(fetchProductDetailsQuery.data.importDate) : null,
                exportDate: fetchProductDetailsQuery.data.exportDate ? new Date(fetchProductDetailsQuery.data.exportDate) : null
            })
        }
    }, [ fetchProductDetailsQuery?.data ]);

    const generateSendData = useCallback((data) => {
        return {
            name: data.name.value,
            productName: data.productName,
            legal_entity_ids: data.legal_entity_ids.map((legal) => legal.value),
            lotNumber: data.lotNumber,
            internalProductId: data.internalProductId,

            importDate: data.importDate,
            importFromCountry: data.importFromCountry?.value,
            exportDate: data.exportDate,
            exportToCountry: data.exportToCountry?.value,
            metricTonnes: data.metricTonnes,
            containerNumber: data.containerNumber,
            shipmentNumber: data.shipmentNumber,

            ...(data.name.commodity ? {
                originCountry: data.originCountry.value,
                ddsReferenceNumber: data.ddsReferenceNumber,
                ddsVerificationNumber: data.ddsVerificationNumber,

                geolocationReference: data.geolocationReference,
                farmGeolocationReference: data.farmGeolocationReference,
                slaughterGeolocationReference: data.slaughterGeolocationReference
            } : {}),

            hsCode: data.hsCode,
            knCode: data.knCode,
            taricCode: data.taricCode,
            description: data.description
        }
    }, []);

    const handleSave = useCallback(() => {
        const payload = generateSendData(productData);

        if (productData.id) {
            updateProductMutate({
                id: productData.id,
                payload: payload
            });
        } else {
            createProductMutate({
                ...payload,
                supplier: supplierId
            })
        }
    }, [ 
        createProductMutate, 
        updateProductMutate,
        generateSendData,
        productData,
        supplierId
    ]);

    const saveDataOnTheStorage = () => {
        return new Promise((res, rej) => {
            setUpdateInprocess(true)

            try{
                localStorage.setItem(STORAGE_SAVE_KEY, JSON.stringify(productData));
                res();
            }
            catch(error){
                rej();
            }

            setUpdateInprocess(false)
        });
    }

    useEffect(() => {
        if(exitConfirmPromptOptions.show){
            const continueAction = () => {
                exitConfirmPromptOptions.callbackFunc && exitConfirmPromptOptions.callbackFunc(true);
                resetConfirmPromptState();
            }

            switch (exitConfirmPromptOptions.confirmStatus) {
                case 'saveAndExit':
                        saveDataOnTheStorage()
                        .then(() => continueAction())
                        .catch(() => {
                            toast(t("An error occurred while saving data."), {
                                type: "error",
                            });

                            continueAction();
                        });
                    break;

                case 'discard':
                    removeDataFromStorage();

                    setTimeout(() => {
                        continueAction();
                    }, 250);
                    break;

                case 'continue':
                    hideConfirmPrompt();
                break;
                
                default: break;
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ 
        exitConfirmPromptOptions.confirmStatus,
        t
    ]);

    useEffect(() => {
        if (!productId) {
            const prevData = localStorage.getItem(STORAGE_SAVE_KEY);

            if(prevData){
                try{
                    const parsedData = JSON.parse(prevData);

                    if(parsedData){
                        setProductData(parsedData);
                    }
                }
                catch(error){
                    removeDataFromStorage();
                }
            }
        }
    }, [
        productId
    ]);

    useEffect(() => {
        if (!productId) {
            if (createProductSucceed) {
                updateConfirmPromptReadyToShowState(false)
            }
            else {
                if (Object.keys(productData).length > 0 &&
                    !exitConfirmPromptOptions.readyToShow
                ) {
                    updateConfirmPromptReadyToShowState(true)
                }
            }
        }
    }, [
        productData,
        exitConfirmPromptOptions.readyToShow,
        updateConfirmPromptReadyToShowState,
        productId,
        createProductSucceed
    ]);

    useEffect(() => {
        updateConfirmPromptContent({
            title: (
                <div className="d-flex justify-content-start align-items-center">
                    <RiErrorWarningLine className="me-2 text-warning" size={22} />
                    {t(!!productId ? 'Cancel Product Editing' : 'Cancel Product Creation')}
                </div>
            ),
            message: (
                <>
                    <p className="mb-2 fw-semibold">
                        {t('You have unsaved changes.')}
                    </p>
                    <p className="text-muted mb-0">
                        {t('Would you like to save this product as a draft so you can continue later, or discard your progress?')}
                    </p>
                </>
            ),
            buttons: ({ updateConfirmPromptConfirmStatus }) => {
                return (
                    <>
                        <Button
                            color='secondary'
                            onClick={() => updateConfirmPromptConfirmStatus('continue')}
                            disabled={updateInprocess}
                        >
                            {t('Continue Editing')}
                        </Button>

                        <Button
                            color='primary'
                            onClick={() => updateConfirmPromptConfirmStatus('saveAndExit')}
                            disabled={updateInprocess}
                        >
                            {updateInprocess && (
                                <Spinner
                                    size="sm"
                                    color="white"
                                    className="me-1"
                                />
                            )}
                            {t('Save as Draft & Exit')}
                        </Button>

                        <Button
                            color='danger'
                            onClick={() => updateConfirmPromptConfirmStatus('discard')}
                            disabled={updateInprocess}
                        >
                            {t('Discard Changes')}
                        </Button>
                    </>
                )
            }
        })
    }, [
        t,
        updateInprocess,
        updateConfirmPromptContent,
        productId
    ]);

    const handleGoToNextTab = () => {
        const numberOfSteps = steps.length
        const currentStepIndex = steps.findIndex((step) => step.stepUniqueKey === tab)

        if (currentStepIndex < numberOfSteps - 1) {
            const nextStep = steps[currentStepIndex + 1]

            setTab(nextStep.stepUniqueKey)
        }
    }

    const handleGoPrevTab = () => {
        const currentStepIndex = steps.findIndex((step) => step.stepUniqueKey === tab)

        if (currentStepIndex > 0) {
            const prevStep = steps[currentStepIndex - 1]

            setTab(prevStep.stepUniqueKey)
        }
    }

    const handleGoToTab = (stepUniqueKey) => {
        setTab(stepUniqueKey)
    }

    const handleCancelButton = () => {
        if (productId) {
            setShowCancelConfirmModal(true);
        }
        else {
            history.push({
                search: 'tab=products'
            })
        }
    }

    return (
        <ProductContext.Provider 
            value={{
                productData,
                setProductData,
                productsList,
                entitiesList,
                countriesList,
                handleSave,
                fetchingDetails: fetchProductDetailsQuery.isFetching,
                submitIsInProcess: productCreationIsLoading || productUpdateIsLoading
            }}
        >
            <div className='create-new-product-page'>
                <Row>
                    <Col sm="12">
                        <CreateProductSteps
                            activeTab={tab}
                            disabledTabs={tabsToDisable}
                        />
                    </Col>

                    <Col sm="12">
                        <CreateProductTabContent
                            activeTab={tab}
                            onNextButtonClicked={handleGoToNextTab}
                            onBackButtonClicked={handleGoPrevTab}
                            onCancelButtonClicked={handleCancelButton}
                            goToTab={handleGoToTab}
                        />
                    </Col>
                </Row>
            </div>

            <CancelConfirmationModal
                isEdit={!!productId}
                isOpen={showCancelConfirmModal}
                toggle={() => setShowCancelConfirmModal(false)}
            />
        </ProductContext.Provider>
    );
};

const mapStatetoProps = (state) => {
    const { token } = state.Login;
    const { Organization , ExitConfirmPrompt } = state;
    return {
        user: state.Login.user,
        token: token,
        organization: Organization,
        exitConfirmPromptOptions: ExitConfirmPrompt
    };
};

export default withNamespaces()(
    withRouter(connect(
        mapStatetoProps,
        {
            updateConfirmPromptReadyToShowState: (payload) => confrimPromptUpdateReadyToShowState(payload),
            updateConfirmPromptContent: (payload) => confirmPromptUpdateContentState(payload),
            resetConfirmPromptState: () => confirmPromptResetState(),
            hideConfirmPrompt: () => confirmPromptHidePrompt()
        }
    )(CreateProductPage))
);